import language from 'config/language';
import { useAppSelector } from 'hooks/reduxHooks';
import { get } from 'lodash-es';

import useMismatchError from './useMismatchError';

type UseNoAccountError = {
  hasNoAccountError: boolean;
  dismissNoAccountError: () => void;
  noAccountErrorMessage: string;
};

/**
 * Detects whether a successfully logged in microsoft 365 user
 * has no account.  The hook inspects state to see
 * if there is a user email (indicating that a token was
 * successfully obtained) with the hasAccount state value
 * being false - indicating that the user.me call failed.
 *
 * @returns
 * @property hasNoAccountError: true if this error has obtained.
 * @property dismissNoAccountError: function to change hasNoAccountError state to false
 * @property noAccountErrorMessage: error message to display to the user.
 */
const useNoAccountError = (): UseNoAccountError => {
  /** Email of user from current stored token */
  const email = useAppSelector((state) => get(state, 'auth.user.email', null));

  /**
   * User has a beecastle account.
   * Null means either no token has been received
   * or user/me call hasn't completed.
   * */
  const hasAccount = useAppSelector((state) =>
    get(state, 'auth.hasAccount', null),
  );

  const { mismatchError, setMismatchFalse } = useMismatchError(
    !!(email && hasAccount === false),
  );

  const noAccountErrorMessage: string =
    language.auth.microsoft_login.errors.no_account;

  return {
    hasNoAccountError: mismatchError,
    dismissNoAccountError: setMismatchFalse,
    noAccountErrorMessage,
  };
};

export default useNoAccountError;
